/**
 * Build a 2D flight path from already-generated application strips.
 *
 * Input points use GeoJSON order [longitude, latitude] by default. Set
 * coordinateOrder to "leaflet" for [latitude, longitude]. Point objects with
 * { lat, lng } are also accepted. The returned path always uses Leaflet order.
 * Segments with the same explicit key form one strip; gaps are crossed straight.
 */
function buildFlightPath2D(input) {
    input = input || {};

    function finite(value) {
        const number = Number(value);
        return Number.isFinite(number) ? number : null;
    }

    function normalizePoint(raw, coordinateOrder) {
        if (Array.isArray(raw) && raw.length >= 2) {
            const first = finite(raw[0]);
            const second = finite(raw[1]);
            if (first === null || second === null) return null;

            return coordinateOrder === 'leaflet'
                ? [first, second]
                : [second, first];
        }

        if (raw && typeof raw === 'object') {
            const lat = finite(raw.lat !== undefined ? raw.lat : raw.latitude);
            const lng = finite(raw.lng !== undefined ? raw.lng : (raw.lon !== undefined ? raw.lon : raw.longitude));
            return lat === null || lng === null ? null : [lat, lng];
        }

        return null;
    }

    function distanceSquared(pointA, pointB) {
        const meanLatitude = ((pointA[0] + pointB[0]) / 2) * Math.PI / 180;
        const latitudeDelta = pointA[0] - pointB[0];
        const longitudeDelta = (pointA[1] - pointB[1]) * Math.cos(meanLatitude);
        return (latitudeDelta * latitudeDelta) + (longitudeDelta * longitudeDelta);
    }

    function projection(referencePoint) {
        const radians = Math.PI / 180;
        const metersPerDegreeLatitude = 111320;
        const metersPerDegreeLongitude = Math.max(1, Math.abs(metersPerDegreeLatitude * Math.cos(referencePoint[0] * radians)));

        return {
            toXY: point => ({
                x: (point[1] - referencePoint[1]) * metersPerDegreeLongitude,
                y: (point[0] - referencePoint[0]) * metersPerDegreeLatitude
            }),
            toLatLng: point => [
                referencePoint[0] + (point.y / metersPerDegreeLatitude),
                referencePoint[1] + (point.x / metersPerDegreeLongitude)
            ]
        };
    }

    function normalizeVector(vector) {
        const length = Math.hypot(vector.x, vector.y);
        if (!Number.isFinite(length) || length < 0.00001) return { x: 1, y: 0 };
        return { x: vector.x / length, y: vector.y / length };
    }

    function directionBetween(fromPoint, toPoint) {
        const localProjection = projection(fromPoint);
        const fromXY = localProjection.toXY(fromPoint);
        const toXY = localProjection.toXY(toPoint);
        return normalizeVector({ x: toXY.x - fromXY.x, y: toXY.y - fromXY.y });
    }

    function offsetPoint(point, direction, distanceMeters) {
        const localProjection = projection(point);
        const xy = localProjection.toXY(point);
        const normalized = normalizeVector(direction);
        return localProjection.toLatLng({
            x: xy.x + (normalized.x * distanceMeters),
            y: xy.y + (normalized.y * distanceMeters)
        });
    }

    function buildBalloonPath(fromPoint, toPoint, currentDirection, nextDirection, stripWidth, loopWidth, loopLength, geometryReferencePoint, maxRepositionDistance) {
        const safeWidth = stripWidth > 0 ? stripWidth : 30;
        const safeLoopWidth = loopWidth > 0 ? loopWidth : 400;
        const safeLoopLength = loopLength > 0 ? loopLength : 600;
        const localProjection = projection(fromPoint);
        const dirA = normalizeVector(currentDirection);
        const nextDir = normalizeVector(nextDirection);
        const normalA = { x: -dirA.y, y: dirA.x };
        const startXY = localProjection.toXY(fromPoint);
        const endXY = localProjection.toXY(toPoint);
        // Size the turn from its original approach point; the actual point only repositions it.
        const geometryEndXY = localProjection.toXY(geometryReferencePoint || toPoint);
        const geometryDelta = { x: geometryEndXY.x - startXY.x, y: geometryEndXY.y - startXY.y };
        const actualDelta = { x: endXY.x - startXY.x, y: endXY.y - startXY.y };
        const forwardDelta = (geometryDelta.x * dirA.x) + (geometryDelta.y * dirA.y);
        const lateralDelta = (geometryDelta.x * normalA.x) + (geometryDelta.y * normalA.y);
        const endForwardDelta = (actualDelta.x * dirA.x) + (actualDelta.y * dirA.y);
        const endLateralDelta = (actualDelta.x * normalA.x) + (actualDelta.y * normalA.y);
        const lateralGap = Math.abs(lateralDelta);
        const turnCross = (dirA.x * nextDir.y) - (dirA.y * nextDir.x);
        const naturalSideSign = lateralDelta >= 0 ? 1 : -1;
        const turnSideSign = Math.abs(turnCross) > 0.02 ? (turnCross >= 0 ? 1 : -1) : naturalSideSign;
        const sideSign = lateralGap < safeLoopWidth * 0.35 ? turnSideSign : naturalSideSign;
        const balloonWidth = Math.max(safeLoopWidth, lateralGap + (safeWidth * 0.75), safeWidth * 4);
        const balloonLength = Math.max(safeLoopLength, Math.abs(forwardDelta) + (safeWidth * 4.1), safeWidth * 9);
        const balloonY = sideSign * balloonWidth;
        const endLocal = { x: endForwardDelta, y: endLateralDelta };
        const radiusY = balloonWidth / 2;
        const radiusX = Math.max(safeWidth * 3, Math.min(balloonLength * 0.33, balloonWidth * 0.68));
        const nextDirLocal = normalizeVector({
            x: (nextDir.x * dirA.x) + (nextDir.y * dirA.y),
            y: (nextDir.x * normalA.x) + (nextDir.y * normalA.y)
        });
        const closeParallelReturn = lateralGap < safeLoopWidth * 0.35 &&
            nextDirLocal.x < -0.98 && Math.abs(nextDirLocal.y) < 0.02;
        const returnRadiusX = radiusX * Math.abs(balloonY - endLocal.y) / balloonWidth;
        const finalLead = Math.max(
            125,
            Math.min(balloonLength * 0.44, balloonWidth * 0.80, safeWidth * 10.5),
            closeParallelReturn ? returnRadiusX * 2 : 0
        );
        const centerX = Math.max(
            balloonLength - radiusX,
            endLocal.x + finalLead + (safeWidth * 1.4),
            radiusX + (safeWidth * 3.2)
        );
        const topJoinX = endLocal.x + finalLead;
        // Move the unchanged turn toward its 125 m lead-in target without crossing the next strip start.
        const targetLeadIn = 125;
        const leadInDiscriminant = Math.pow(2 * centerX * nextDirLocal.x, 2) -
            (4 * ((centerX * centerX) - (targetLeadIn * targetLeadIn)));
        const targetReposition = centerX > targetLeadIn && leadInDiscriminant >= 0
            ? (-2 * centerX * nextDirLocal.x - Math.sqrt(leadInDiscriminant)) / 2
            : maxRepositionDistance;
        const maxUsefulReposition = Math.max(0, -centerX * nextDirLocal.x);
        const repositionDistance = Math.min(
            Math.max(0, maxRepositionDistance || 0),
            Math.max(0, targetReposition || 0),
            maxUsefulReposition
        );
        const curveOffset = {
            x: nextDirLocal.x * repositionDistance,
            y: nextDirLocal.y * repositionDistance
        };

        const toLatLngLocal = localPoint => {
            const worldPoint = {
                x: startXY.x + (dirA.x * localPoint.x) + (normalA.x * localPoint.y),
                y: startXY.y + (dirA.y * localPoint.x) + (normalA.y * localPoint.y)
            };
            return localProjection.toLatLng(worldPoint);
        };
        const bezierPoint = (p0, p1, p2, p3, t) => {
            const inverse = 1 - t;
            const inverseSquared = inverse * inverse;
            const inverseCubed = inverseSquared * inverse;
            const tSquared = t * t;
            const tCubed = tSquared * t;
            return {
                x: (inverseCubed * p0.x) + (3 * inverseSquared * t * p1.x) + (3 * inverse * tSquared * p2.x) + (tCubed * p3.x),
                y: (inverseCubed * p0.y) + (3 * inverseSquared * t * p1.y) + (3 * inverse * tSquared * p2.y) + (tCubed * p3.y)
            };
        };
        const curve = [];
        const addLine = (from, to, spacingMeters) => {
            const distance = Math.hypot(to.x - from.x, to.y - from.y);
            const steps = Math.max(1, Math.ceil(distance / Math.max(8, spacingMeters)));
            for (let i = 1; i <= steps; i++) {
                const factor = i / steps;
                curve.push({
                    x: from.x + ((to.x - from.x) * factor),
                    y: from.y + ((to.y - from.y) * factor)
                });
            }
        };
        const addBezier = (p0, p1, p2, p3, steps) => {
            const safeSteps = Math.max(16, steps);
            for (let i = 1; i <= safeSteps; i++) curve.push(bezierPoint(p0, p1, p2, p3, i / safeSteps));
        };

        const bottomArcPoint = { x: centerX + curveOffset.x, y: curveOffset.y };
        const topArcPoint = { x: centerX + curveOffset.x, y: balloonY + curveOffset.y };
        const topJoinPoint = { x: topJoinX + curveOffset.x, y: balloonY + curveOffset.y };
        const preferDirectArcExit = Math.abs(nextDirLocal.y) > 0.58 && Math.abs(nextDirLocal.x) < 0.72;

        addLine({ x: 0, y: 0 }, bottomArcPoint, 14);

        const centerY = (balloonY / 2) + curveOffset.y;
        const arcSteps = Math.max(64, Math.min(150, Math.ceil((Math.PI * Math.max(radiusX, radiusY)) / 7)));
        const startAngle = sideSign >= 0 ? (-Math.PI / 2) : (Math.PI / 2);
        const endAngle = sideSign >= 0 ? (Math.PI / 2) : (-Math.PI / 2);
        for (let i = 1; i <= arcSteps; i++) {
            const angle = startAngle + (((endAngle - startAngle) * i) / arcSteps);
            curve.push({
                x: centerX + curveOffset.x + (radiusX * Math.cos(angle)),
                y: centerY + (radiusY * Math.sin(angle))
            });
        }

        if (!preferDirectArcExit && centerX > topJoinX) addLine(topArcPoint, topJoinPoint, 14);

        const exitStartPoint = preferDirectArcExit ? topArcPoint : topJoinPoint;
        const curveEndPoint = {
            x: endLocal.x + curveOffset.x,
            y: endLocal.y + curveOffset.y
        };
        if (closeParallelReturn) {
            // Match the upper ellipse with two quarter turns, allowing enough forward room for both.
            const returnRadiusY = Math.abs(exitStartPoint.y - curveEndPoint.y) / 2;
            const returnSide = exitStartPoint.y >= curveEndPoint.y ? 1 : -1;
            const returnStartPoint = { x: curveEndPoint.x + returnRadiusX * 2, y: exitStartPoint.y };
            const middlePoint = {
                x: curveEndPoint.x + returnRadiusX,
                y: (exitStartPoint.y + curveEndPoint.y) / 2
            };
            const handleRatio = 4 * (Math.sqrt(2) - 1) / 3;
            const returnSteps = Math.max(24, Math.min(100, Math.ceil(Math.PI * Math.max(returnRadiusX, returnRadiusY) / 14)));
            if (exitStartPoint.x > returnStartPoint.x) addLine(exitStartPoint, returnStartPoint, 14);
            addBezier(
                returnStartPoint,
                { x: returnStartPoint.x - returnRadiusX * handleRatio, y: returnStartPoint.y },
                { x: middlePoint.x, y: middlePoint.y + returnSide * returnRadiusY * handleRatio },
                middlePoint,
                returnSteps
            );
            addBezier(
                middlePoint,
                { x: middlePoint.x, y: middlePoint.y - returnSide * returnRadiusY * handleRatio },
                {
                    x: curveEndPoint.x - nextDirLocal.x * returnRadiusX * handleRatio,
                    y: curveEndPoint.y - nextDirLocal.y * returnRadiusX * handleRatio
                },
                curveEndPoint,
                returnSteps
            );
        } else {
            const finalDistance = Math.hypot(exitStartPoint.x - curveEndPoint.x, exitStartPoint.y - curveEndPoint.y);
            const finalHandleX = Math.max(80, Math.min(finalLead * 0.62, finalDistance * 0.55));
            const endTangentDistance = Math.max(95, Math.min(finalDistance * 0.74, finalLead * 1.22, balloonWidth * 0.74, safeWidth * 12));
            addBezier(
                exitStartPoint,
                {
                    x: exitStartPoint.x - finalHandleX,
                    y: preferDirectArcExit
                        ? exitStartPoint.y - (sideSign * Math.min(balloonWidth * 0.10, finalDistance * 0.10))
                        : exitStartPoint.y
                },
                {
                    x: curveEndPoint.x - (nextDirLocal.x * endTangentDistance),
                    y: curveEndPoint.y - (nextDirLocal.y * endTangentDistance)
                },
                curveEndPoint,
                Math.max(24, Math.ceil(finalDistance / 12))
            );
        }

        return {
            path: curve.map(toLatLngLocal),
            repositionDistance: repositionDistance
        };
    }

    function samePoint(pointA, pointB) {
        return pointA && pointB &&
            Math.abs(pointA[0] - pointB[0]) < 1e-12 &&
            Math.abs(pointA[1] - pointB[1]) < 1e-12;
    }

    function cloneLine(line, index, coordinateOrder) {
        let rawPoints = Array.isArray(line)
            ? line
            : (line && (line.coordinates || line.points || line.line || line.geometry));
        if (rawPoints && !Array.isArray(rawPoints)) {
            if (rawPoints.type === 'Feature' && rawPoints.geometry) rawPoints = rawPoints.geometry;
            rawPoints = rawPoints && rawPoints.coordinates;
        }
        if (!Array.isArray(rawPoints)) return null;

        const points = [];
        for (let i = 0; i < rawPoints.length; i++) {
            const point = normalizePoint(rawPoints[i], coordinateOrder);
            if (point) points.push(point);
        }
        if (points.length < 2) return null;

        const rawKey = line && !Array.isArray(line) ? line.key : null;
        const keyValue = rawKey === null || rawKey === undefined || rawKey === '' ? null : finite(rawKey);
        return {
            key: keyValue === null ? index : keyValue,
            keyIsExplicit: keyValue !== null,
            points: points
        };
    }

    function joinStripSegments(lines) {
        const groups = [];
        const groupsByKey = new Map();
        lines.forEach(line => {
            let group = line.keyIsExplicit ? groupsByKey.get(line.key) : null;
            if (!group) {
                group = { key: line.key, segments: [] };
                groups.push(group);
                if (line.keyIsExplicit) groupsByKey.set(line.key, group);
            }
            group.segments.push(line.points);
        });

        return groups.map(group => {
            if (group.segments.length === 1) return { key: group.key, points: group.segments[0] };

            const reference = group.segments.find(points => !samePoint(points[0], points[points.length - 1])) || group.segments[0];
            const localProjection = projection(reference[0]);
            const direction = directionBetween(reference[0], reference[reference.length - 1]);
            const position = point => {
                const xy = localProjection.toXY(point);
                return (xy.x * direction.x) + (xy.y * direction.y);
            };
            const segments = group.segments.map(points => {
                const first = position(points[0]);
                const last = position(points[points.length - 1]);
                return {
                    position: Math.min(first, last),
                    points: last < first ? points.slice().reverse() : points
                };
            }).sort((left, right) => left.position - right.position);
            const points = [];
            segments.forEach(segment => {
                segment.points.forEach(point => {
                    if (!samePoint(points[points.length - 1], point)) points.push(point);
                });
            });
            return { key: group.key, points: points };
        });
    }

    function orderLines(lines, strategy, lineIndex, loopWidth, stripWidth) {
        const mode = String(strategy || 'BACKTOBACK').toUpperCase();

        if (mode === 'BACKTOBACK') return lines.slice();

        if (mode === 'SKIPBACKTOBACK') {
            const odd = lines.filter(item => item.key % 2 === 1 || item.key % 2 === -1);
            const even = lines.filter(item => item.key % 2 === 0);
            return lineIndex % 2 === 0 ? odd.concat(even) : even.concat(odd);
        }

        if (mode === 'QUICKRACETRACK') {
            const ordered = [];
            const half = Math.ceil(lines.length / 2);
            for (let i = 0; i < half; i++) {
                ordered.push(lines[i]);
                if (lines.length > half + i) ordered.push(lines[half + i]);
            }
            return ordered;
        }

        if (mode === 'REVERSERACETRACK') {
            const ordered = [];
            const half = Math.ceil(lines.length / 2);
            const start = Math.floor(lines.length / 2);
            for (let i = 0; i < half; i++) {
                ordered.push(lines[start + i]);
                if (i < start) ordered.push(lines[i]);
            }
            return ordered;
        }

        if (mode === 'RACETRACK') {
            if (
                loopWidth === null || loopWidth <= 0 ||
                stripWidth === null || stripWidth <= 0 ||
                loopWidth * 2 >= lines.length * stripWidth
            ) {
                const fallback = orderLines(lines, 'QUICKRACETRACK', lineIndex, loopWidth, stripWidth);
                return fallback;
            }

            const linesPerSide = Math.max(1, Math.ceil(loopWidth / stripWidth));
            const groupSize = linesPerSide * 2;
            const partCount = Math.floor(lines.length / groupSize);
            if (partCount <= 0) {
                const fallback = orderLines(lines, 'QUICKRACETRACK', lineIndex, loopWidth, stripWidth);
                return fallback;
            }

            let ordered = [];
            let finalIndex = -1;
            for (let i = 1; i <= partCount; i++) {
                const end = (i * groupSize) - 1;
                const start = end + 1 - groupSize;
                finalIndex = end;
                const group = lines.slice(start, end + 1);
                ordered = ordered.concat(orderLines(
                    group,
                    ordered.length > 0 ? 'REVERSERACETRACK' : 'QUICKRACETRACK',
                    lineIndex,
                    loopWidth,
                    stripWidth
                ));
            }

            const remainder = lines.slice(finalIndex + 1);
            return ordered.concat(orderLines(
                remainder,
                'REVERSERACETRACK',
                lineIndex,
                loopWidth,
                stripWidth
            ));
        }

        if (mode === 'EXPAND') {
            const ordered = [];
            const size = lines.length;
            const half = Math.floor(size / 2);
            for (let i = 0; i < half; i++) {
                if (lineIndex % 2 === 0) ordered.push(lines[(size - half) + i]);
                if (size - i > half) ordered.push(lines[(size - half - 1) - i]);
                if (lineIndex % 2 !== 0) ordered.push(lines[(size - half) + i]);
            }
            return ordered;
        }

        if (mode === 'SQUEEZE') {
            const ordered = [];
            const size = lines.length;
            const half = Math.floor(size / 2);
            for (let i = 0; i < half; i++) {
                ordered.push(lines[i]);
                if (size - i > half) ordered.push(lines[size - 1 - i]);
            }
            return ordered;
        }

        return lines.slice();
    }

    const coordinateOrder = input.coordinateOrder === 'leaflet' ? 'leaflet' : 'geojson';
    const rawLines = Array.isArray(input.passes)
        ? input.passes
        : (Array.isArray(input.faixas) ? input.faixas : []);
    const lines = [];
    for (let i = 0; i < rawLines.length; i++) {
        const line = cloneLine(rawLines[i], i, coordinateOrder);
        if (line) lines.push(line);
    }

    const lineIndexValue = finite(input.lineIndex);
    const lineIndex = lineIndexValue === null ? 0 : Math.trunc(lineIndexValue);
    const strategyMode = String(input.strategy || 'BACKTOBACK').toUpperCase();
    const loopWidth = finite(input.loopWidthM !== undefined ? input.loopWidthM : input.size_loop);
    const loopLength = finite(input.loopLengthM !== undefined ? input.loopLengthM : input.size_h_loop);
    const stripWidth = finite(input.stripWidthM !== undefined ? input.stripWidthM : input.faixa);
    const orderedLines = orderLines(joinStripSegments(lines), strategyMode, lineIndex, loopWidth, stripWidth);
    const startPoint = normalizePoint(input.startPoint || input.start, coordinateOrder);
    const endPoint = normalizePoint(input.endPoint || input.end, coordinateOrder);
    const path = [];

    function append(point) {
        if (point && !samePoint(path[path.length - 1], point)) path.push([point[0], point[1]]);
    }

    let currentPoint = null;
    let currentDirection = null;
    let balloonCount = 0;
    for (let i = 0; i < orderedLines.length; i++) {
        const points = orderedLines[i].points;
        const first = points[0];
        const last = points[points.length - 1];
        let reverse = false;

        if (i === 0 && startPoint) {
            reverse = distanceSquared(startPoint, last) < distanceSquared(startPoint, first);
        } else if (currentPoint) {
            reverse = distanceSquared(currentPoint, last) < distanceSquared(currentPoint, first);
            const nextStart = reverse ? last : first;
            const nextDirection = directionBetween(
                nextStart,
                reverse ? points[points.length - 2] : points[1]
            );
            const directionDot = (currentDirection.x * nextDirection.x) +
                (currentDirection.y * nextDirection.y);

            // Keep the nearest endpoint unless it would continue in the same direction.
            if (directionDot > 0) reverse = !reverse;
        } else if (i === orderedLines.length - 1 && endPoint) {
            reverse = distanceSquared(endPoint, first) < distanceSquared(endPoint, last);
        }

        const oriented = reverse ? points.slice().reverse() : points;
        if (i === 0 && startPoint) append(startPoint);

        if (i > 0 && currentPoint && oriented.length >= 2) {
            const nextStart = oriented[0];
            const nextDirection = directionBetween(nextStart, oriented[1]);
            const approachDirection = {
                x: -nextDirection.x,
                y: -nextDirection.y
            };
            const configuredApproachDistance = loopLength > 0
                ? loopLength
                : (loopWidth > 0 ? loopWidth : Math.max(240, (stripWidth || 30) * 8));
            const approachInset = Math.min(
                configuredApproachDistance,
                Math.max((stripWidth || 30) * 3, configuredApproachDistance * 0.5)
            );
            const geometryReferencePoint = offsetPoint(nextStart, approachDirection, configuredApproachDistance);
            const approachPoint = offsetPoint(nextStart, approachDirection, approachInset);
            const previousDirection = currentDirection || directionBetween(oriented[0], oriented[1]);
            const balloonPath = buildBalloonPath(
                currentPoint,
                approachPoint,
                previousDirection,
                nextDirection,
                stripWidth,
                loopWidth,
                loopLength,
                geometryReferencePoint,
                approachInset
            );

            for (let j = 0; j < balloonPath.path.length; j++) append(balloonPath.path[j]);

            const remainingApproachDistance = Math.max(0, approachInset - balloonPath.repositionDistance);
            const adjustedApproachPoint = offsetPoint(nextStart, approachDirection, remainingApproachDistance);
            const approachDistance = Math.sqrt(distanceSquared(adjustedApproachPoint, nextStart)) * 111320;
            const approachSteps = Math.max(1, Math.ceil(approachDistance / 20));
            for (let j = 1; j <= approachSteps; j++) {
                const factor = j / approachSteps;
                append([
                    adjustedApproachPoint[0] + ((nextStart[0] - adjustedApproachPoint[0]) * factor),
                    adjustedApproachPoint[1] + ((nextStart[1] - adjustedApproachPoint[1]) * factor)
                ]);
            }
            balloonCount++;
        }

        for (let j = 0; j < oriented.length; j++) append(oriented[j]);
        currentPoint = oriented[oriented.length - 1];
        currentDirection = directionBetween(oriented[Math.max(0, oriented.length - 2)], currentPoint);
    }

    if (endPoint) append(endPoint);

    return {
        path: path,
        passCount: orderedLines.length,
        balloonCount: balloonCount,
        strategy: strategyMode
    };
}

/**
 * Generate a Leaflet-ready path off the UI thread where Web Workers are allowed.
 * The input contains already-generated strip lines, not just a polygon outline.
 * Balloon turns connect strips using loopWidthM/size_loop and loopLengthM/size_h_loop.
 *
 * @example
 * generateFlightPath2D({
 *   passes: [{ key: 0, coordinates: [[-46.5, -23.5], [-46.49, -23.5]] }],
 *   strategy: 'BACKTOBACK'
 * }).then(({ path }) => L.polyline(path));
 */
export function generateFlightPath2D(input) {
    return new Promise((resolve, reject) => {
        if (
            typeof Worker === 'undefined' ||
            typeof Blob === 'undefined' ||
            typeof URL === 'undefined' ||
            typeof URL.createObjectURL !== 'function'
        ) {
            try {
                resolve(buildFlightPath2D(input));
            } catch (error) {
                reject(error);
            }
            return;
        }

        let worker = null;
        let workerUrl = null;
        let completed = false;

        const cleanup = () => {
            if (worker) worker.terminate();
            if (workerUrl) URL.revokeObjectURL(workerUrl);
            worker = null;
            workerUrl = null;
        };

        const fallback = () => {
            if (completed) return;
            completed = true;
            cleanup();
            try {
                resolve(buildFlightPath2D(input));
            } catch (error) {
                reject(error);
            }
        };

        try {
            const workerSource = [
                'var buildFlightPath2D = ' + buildFlightPath2D.toString() + ';',
                'self.onmessage = function(event) {',
                '  try { self.postMessage({ result: buildFlightPath2D(event.data) }); }',
                '  catch (error) { self.postMessage({ error: error && error.message ? error.message : String(error) }); }',
                '};'
            ].join('\n');
            workerUrl = URL.createObjectURL(new Blob([workerSource], { type: 'application/javascript' }));
            worker = new Worker(workerUrl);
            worker.onmessage = (event) => {
                if (completed) return;
                completed = true;
                cleanup();
                if (event.data && event.data.error) {
                    reject(new Error(event.data.error));
                    return;
                }
                resolve(event.data && event.data.result);
            };
            worker.onerror = fallback;
            worker.onmessageerror = fallback;
            worker.postMessage(input || {});
        } catch (error) {
            fallback();
        }
    });
}

export default generateFlightPath2D;
